import { useEffect, useState } from 'react';
import { getStylePackById } from '@/themes/stylePacks';
import type { PaywallTimerFormat } from '@/themes/stylePack.types';
import type { PublicQuizStrings } from '@/utils/publicQuizStrings';
import { usePageShown } from '@/public/useAppShownSignal';

/**
 * WEB-925 атом 4: механика таймера резерва вынесена из рендерера, чтобы её
 * могли переиспользовать и блок `countdown`, и дубль таймера в липком хедере
 * пейволла. Поведение (якорь в sessionStorage, дефолт 15 минут, посекундный
 * тик) перенесено байт-в-байт из WEB-890 п.5 — вид таймера не меняется.
 */

export interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/** WEB-890 п.5: дефолтная длительность маркетингового таймера без данных. */
export const COUNTDOWN_DEFAULT_MINUTES = 15;

export function computeCountdown(targetMs: number | null): CountdownTime {
  if (targetMs === null) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const diff = Math.max(0, targetMs - Date.now());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds };
}

/**
 * WEB-890 п.5: целевой момент таймера. targetDate главнее; без него —
 * относительная длительность (countdownMinutes | дефолт 15 мин) с якорем в
 * sessionStorage per block id (перезагрузка не сбрасывает отсчёт). Раньше
 * без targetDate таймер стоял на нулях («таймер но ворк», PM 29.07).
 *
 * WEB-925: якорь по id блока даёт бесплатную синхронизацию дубля в липком
 * хедере — второй инстанс с тем же id читает тот же момент.
 */
export function resolveCountdownTarget(
  blockId: string | undefined,
  targetDate?: string,
  durationMinutes?: number,
): number | null {
  if (targetDate) {
    const t = new Date(targetDate).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const minutes =
    typeof durationMinutes === 'number' && durationMinutes > 0
      ? durationMinutes
      : COUNTDOWN_DEFAULT_MINUTES;
  const key = `qb_cd:${blockId ?? 'anon'}`;
  try {
    const stored = sessionStorage.getItem(key);
    if (stored) {
      const t = Number(stored);
      if (!Number.isNaN(t)) return t;
    }
    const t = Date.now() + minutes * 60_000;
    sessionStorage.setItem(key, String(t));
    return t;
  } catch {
    return Date.now() + minutes * 60_000;
  }
}

/**
 * WEB-3673, переделка 2 — целевой момент таймера на публичной странице с
 * учётом ПОКАЗА. Прогретая SDK страница (`?preload=1`) рисуется заранее и
 * живёт в фоне до часа; отсчёт обязан идти от показа человеку
 * (`usePageShown`), иначе таймер «15 минут» встречает его 04:59 или 00:00.
 * До показа якорь `qb_cd:<id>` НЕ пишется, а цель — полный срок от момента
 * монтирования с `waiting: true` (вызывающий замораживает тик, на экране стоит
 * полный срок). Показ — якорь `resolveCountdownTarget` (сейчас + срок),
 * отсчёт пошёл. Без `preload` — показано сразу, якорь при монтировании, как
 * было. `enabled: false` (превью редактора, таймер выключен) — цели нет.
 * Одна дверь для блока таймера, липкого хедера и промо-билета.
 */
export function usePublicCountdownTarget(
  blockId: string | undefined,
  targetDate?: string,
  durationMinutes?: number,
  enabled = true,
): { targetMs: number | null; waiting: boolean } {
  const shown = usePageShown();
  const [anchored, setAnchored] = useState<number | null>(() =>
    enabled && shown
      ? resolveCountdownTarget(blockId, targetDate, durationMinutes)
      : null,
  );
  // Цель «до показа» считается один раз: новое число на каждом рендере
  // перезапускало бы тик useCountdown.
  const [beforeShown] = useState(() =>
    resolveLiveCountdownTarget(targetDate, durationMinutes),
  );
  useEffect(() => {
    if (!enabled || !shown || anchored !== null) return;
    setAnchored(resolveCountdownTarget(blockId, targetDate, durationMinutes));
  }, [enabled, shown, anchored, blockId, targetDate, durationMinutes]);
  if (!enabled) return { targetMs: null, waiting: false };
  if (anchored !== null) return { targetMs: anchored, waiting: false };
  return { targetMs: beforeShown, waiting: true };
}

/**
 * WEB-978 п.6: целевой момент таймера в ПРЕВЬЮ редактора — живой отсчёт от
 * «сейчас», БЕЗ якоря sessionStorage. targetDate по-прежнему главнее.
 *
 * Причина отдельной функции: resolveCountdownTarget всегда читает якорь по
 * blockId, если он уже лежит в sessionStorage — это специально для публики
 * (перезагрузка не сбрасывает срок, WEB-925). Тот же blockId переиспользуется,
 * пока автор работает над воронкой в превью; если работать дольше настроенного
 * лимита таймера, якорь протухает и resolveCountdownTarget навсегда отдаёт
 * прошедший момент (computeCountdown клэмпит его в 00:00:00). Смена «Срок,
 * минут» тоже не лечила, потому что якорь читается независимо от текущих
 * пропов. Эта функция ничего не кладёт и не читает из sessionStorage — вызов
 * с новыми пропами всегда даёт свежий момент.
 */
export function resolveLiveCountdownTarget(
  targetDate?: string,
  durationMinutes?: number,
): number {
  if (targetDate) {
    const t = new Date(targetDate).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const minutes =
    typeof durationMinutes === 'number' && durationMinutes > 0
      ? durationMinutes
      : COUNTDOWN_DEFAULT_MINUTES;
  return Date.now() + minutes * 60_000;
}

/**
 * Посекундный тик до заданного момента (та же механика, что была в блоке).
 *
 * WEB-978 п.6: `time` пересчитывается сразу при смене targetMs, а не только
 * следующим тиком интервала — иначе смена «Срок, минут» в превью редактора
 * визуально «не срабатывала» до целой секунды (targetMs уже новый, а time
 * ещё старый, потому что интервал с новым targetMs только-только пересоздан
 * и его первый setTime случится через 1000мс).
 */
export function useCountdown(
  targetMs: number | null,
  /** WEB-2996: замороженный рендер (узел графа) — значение есть, тика нет. */
  frozen?: boolean,
): CountdownTime {
  const [time, setTime] = useState(() => computeCountdown(targetMs));
  useEffect(() => {
    setTime(computeCountdown(targetMs));
    if (frozen) return undefined;
    const interval = setInterval(() => setTime(computeCountdown(targetMs)), 1000);
    return () => clearInterval(interval);
  }, [targetMs, frozen]);
  return time;
}

/**
 * WEB-925 атом 4: формат таймера резерва (SYNTHESIS.md §6 п.1).
 *
 * `'none'` НЕ означает «пак убивает таймер»: блок ставит автор руками, пак
 * отвечает за форму, а не за наличие. Поэтому 'none' (и отсутствие пака, и
 * выдуманный id) резолвится в 'default' — сегодняшний вид блока. Тот же
 * принцип, что у `accents.premium: 'none'` в атоме 3.
 */
export type CountdownFormat = 'default' | 'mono-header' | 'digits-labeled';

export function resolveCountdownFormat(stylePackId?: string): CountdownFormat {
  const pack = stylePackId ? getStylePackById(stylePackId) : undefined;
  const timer: PaywallTimerFormat | undefined = pack?.paywall.timer;
  if (timer === 'mono-header') return 'mono-header';
  if (timer === 'digits-labeled') return 'digits-labeled';
  return 'default';
}

/** Размер цифр формата, когда автор не задал свой (WEB-696 QA: дефолт 36). */
export const COUNTDOWN_FORMAT_DIGIT_SIZE: Record<CountdownFormat, number> = {
  default: 36,
  // 12min: моно 28px в липком хедере и в теле.
  'mono-header': 28,
  // BetterMe: цифры 32/700 с подписями под ними.
  'digits-labeled': 32,
};

/**
 * Подписи под цифрами по формату, на языке воронки.
 *
 * WEB-785 (возврат QA lane D1 06.08): раньше это был статический словарь
 * с зашитым английским ('Days'/'Hours'/'Min'/'Sec') — на русской генерации
 * таймер срочности на публичном пейволле показывал английские подписи
 * поверх русского контента. Подписи теперь берутся из общего словаря
 * служебных строк паблик-рендера (publicQuizStrings.ts), который вызывающий
 * уже резолвит по varCtx.system.locale — та же линия, что у остальных
 * служебных строк квиза/пейволла (счётчик шагов, «до/после», отзывы).
 */
export function countdownLabelsFor(
  format: CountdownFormat,
  strings: Pick<PublicQuizStrings, 'countdownLabelsDefault' | 'countdownLabelsDigitsLabeled'>,
): string[] {
  if (format === 'digits-labeled') return strings.countdownLabelsDigitsLabeled;
  if (format === 'mono-header') return [];
  return strings.countdownLabelsDefault;
}

/** Моно-шрифт строки 12min (шрифт цифр автора, если он задан, важнее). */
export const COUNTDOWN_MONO_FAMILY =
  "'SF Mono', 'Roboto Mono', ui-monospace, monospace";
